import { FeatureLayerUtils } from "./utils/FeatureLayerUtils.js";
import { Layer } from "./Layer.js";

/**
 * Opus Layer Implementation
 * 
 * @author rhess <robin.hess@awi.de>
 * 
 * @memberof vef.map.layer
 */
export class OpusLayer extends Layer {

    /**
     * Set all properties for the layer based on the options object.
     * 
     * @param {object} config 
     * @param {object} cache 
     * @param {string} id 
     */
    constructor(config, cache, id) {
        // calling parent constructor
        super(config, cache, id);

        // init default values and define getters and setters
        this.addDefaults_({
            url: "https://opus.awi.de/rest",
            opacityRatio: 1,
            color: "#91d437",
            // overwrite defaults
            opacitySettings: true,
            metadataTemplate: "#opus",
            hoverEvents: false,
            availableCrs: FeatureLayerUtils.availableCrs,
            icon: "fas fa-draw-polygon"
        });

        this.setAll_("type", "opus");

        // ignore availableCrs and always use the default value
        if ("availableCrs" in this.config) delete this.config.availableCrs;
        if ("availableCrs" in this.cache) delete this.cache.availableCrs;

        // storing an array of clicked features for getFeatureInfo
        this.clickedFeatures_ = [];

        this.events["click"] = [];
        this.events["mouseover"] = [];
        this.events["mouseout"] = [];
    }

    createFeature_(marker) {
        return {
            type: "Feature",
            properties: marker,
            geometry: {
                type: "Point",
                coordinates: [marker.longitude, marker.latitude]
            }
        }
    }

    createLeafletMarkers_(recorders) {
        this.mapLayers_.leaflet.clearLayers();
        const markers = [];
        for (let i = 0; i < recorders.length; ++i) {
            const marker = L.circleMarker({ lat: recorders[i].latitude, lng: recorders[i].longitude }, {
                opacity: this.opacity,
                fillOpacity: this.opacity * this.opacityRatio,
                renderer: FeatureLayerUtils.getCanvasRenderer(),
                color: "#237cc9"
            }).bindTooltip("1", {
                permanent: true,
                opacity: 1,
                direction: "center",
                className: "opus-tooltip-label"
            });
            const fire = (mouseEvent, eventName, addClickedFeature) => {
                const feature = this.createFeature_(recorders[i]);
                if (addClickedFeature) this.clickedFeatures_.push(feature);
                this.fire(eventName, {
                    feature: feature,
                    mouseEvent: mouseEvent
                });
            }
            marker.on("click", e => fire(e, "click", !this.hoverEvents))
            if (this.hoverEvents) {
                marker.on("mouseover", e => fire(e, "mouseover", true));
                marker.on("mouseout", e => fire(e, "mouseout", false));
            }
            markers.push(marker);
        }
        if (markers.length > 0) setTimeout(() => {
            markers.forEach(marker => this.mapLayers_.leaflet.addLayer(marker))
        }, 0);
    }

    /**
     * Create a map layer based on the service options
     * 
     * @param {string} type e.g. "leaflet"
     * @private
     */
    createMapLayer_(type) {
        switch (type) {
            case "leaflet":
                if ("markerClusterGroup" in L) {
                    this.mapLayers_[type] = L.markerClusterGroup({
                        removeOutsideVisibleBounds: false,
                        spiderfyOnMaxZoom: false,
                        showCoverageOnHover: true,
                        zoomToBoundsOnClick: false,
                        maxClusterRadius: 40,
                        iconCreateFunction: function (cluster) {
                            const childCount = cluster.getChildCount()
                            return new L.DivIcon({
                                html: '<div class="opus-cluster"><span>' + childCount + '</span></div>',
                                className: 'marker-cluster',
                                iconSize: [0, 0]
                            })
                        }
                    });
                    this.mapLayers_[type].on('clusterclick', (e) => {
                        e.layer.spiderfy()
                    });
                } else {
                    this.mapLayers_[type] = L.featureGroup();;
                }

                return this.mapLayers_[type];
        }
        return null;
    }

    /**
     * Helper method to update the mapLayer according to
     * the current state. Might be necessary after switching the map type
     * 
     * @private
     */
    updateMapLayer_(type) {
        this.activeMap_ = type;

        if (!this.recorders_) {
            this.loadRecorders_();
        } else {
            this.applyRecorders_();
        }
    }

    /**
     * load the geojson content of the layer and insert it
     * 
     * @private
     */
    loadRecorders_() {
        if (this.activeRequest_) {
            this.activeRequest_.abort();
            this.activeRequest_ = null;
            this.fire("load");
        }

        this.fire("loading");

        const xhttp = new XMLHttpRequest();
        xhttp.onreadystatechange = () => {
            if (xhttp.readyState == 4) {
                if (xhttp.status == 200) {
                    this.recorders_ = JSON.parse(xhttp.responseText);
                    this.applyRecorders_();
                }
                this.activeRequest_ = null;
                this.fire("load");
            }
        };
        xhttp.open("GET", this.url + "/recorder-positions", true);
        xhttp.send();

        this.activeRequest_ = xhttp;
    }

    /**
     * Internal method to apply the current geojson to the active mapLayer
     * 
     * @private
     */
    applyRecorders_() {
        if (!(this.activeMap_ in this.mapLayers_)) return;

        let recorders = [...this.recorders_];

        if (this.filter) {
            for (let i = 0; i < this.filter.length; ++i) {
                const filter = this.filter[i];
                if (filter.excludedLayers.includes(this.uniqueId)) continue;

                if (filter.values.length == 0) continue;
                for (let j = recorders.length - 1; j >= 0; --j) {
                    const recorder = recorders[j];
                    const column = filter.column;
                    const values = filter.values[0];

                    let matched = true;

                    if (filter.type.toLowerCase() == "time") {
                        const start = new Date(recorder.analysis_start_timestamp).getTime();
                        const end = new Date(recorder.analysis_end_timestamp).getTime();
                        const d1 = new Date(values[1]).getTime();

                        matched = false;
                        switch (values[0].toLowerCase()) {
                            case "bt":
                                const d2 = new Date(values[2]).getTime();
                                if (((d1 <= start) || (d1 <= end)) && ((d1 >= start) || (d2 >= end))) matched = true;
                                break;
                            case "eq":
                            case "lt":
                                if ((d1 >= start) || (d1 >= end)) matched = true;
                                break;
                            case "gt":
                                if ((d1 <= start) || (d1 <= end)) matched = true;
                                break;
                        }
                    }

                    if (matched && (column in recorder)) {
                        if (
                            (values[0].toLowerCase() == "eq") &&
                            !(recorder[column].toString().toLowerCase().includes(values[1].toString().toLowerCase()))
                        ) matched = false;
                    }

                    if (!matched) recorders.splice(j, 1)
                }
            }
        }

        switch (this.activeMap_) {
            case "leaflet":
                this.createLeafletMarkers_(recorders);
                break;
        }
    }

    /**
     * Apply a filter on the layer
     */
    applyFilter() {
        // only apply filter if the layer is active
        if (this.active && this.filter) this.applyRecorders_();
    }

    fetchRecorderMetadata_(feature) {
        return new Promise((resolve) => {
            if ("recorder" in feature.properties) {
                resolve(feature);
            } else {
                const xhttp = new XMLHttpRequest();
                xhttp.onreadystatechange = () => {
                    if (xhttp.readyState == 4) {
                        if (xhttp.status == 200) {
                            const recorder = JSON.parse(xhttp.responseText);
                            Object.assign(feature.properties, recorder);
                        } else {
                            console.error("could not resolve recorder", feature)
                        }
                        // resolve, even if it fails
                        resolve(feature);
                    }
                };
                const r = feature.properties;
                xhttp.open("GET", this.url + `/recorder-metadata/${r.recorderDatasetName}`, true);
                xhttp.send();
            }
        })
    }

    /**
     * Get Info of the last clicked features
     * @returns {Promise} feature info
     */
    getFeatureInfo() {
        return new Promise(resolve => {

            const promises = [];
            this.clickedFeatures_.forEach(feature => {
                promises.push(this.fetchRecorderMetadata_(feature));
            });
            this.clickedFeatures_ = [];

            Promise.all(promises).then(features => {
                resolve({
                    layer: this,
                    data: {
                        type: "FeatureCollection",
                        features: features
                    }
                });
            });
        });
    }

    /**
     * Set the transparency between 0 and 1
     * @override
     * @param {number} opacity
     */
    setOpacity(opacity) {
        FeatureLayerUtils.setOpacity(this, opacity);
    }

    /**
     * Get the bounding box as an object
     * 
     * @returns {object} { min: {x, y}, max: {x, y}, crs: "CRS:84" }
     */
    getBounds() {
        const bounds = FeatureLayerUtils.computeBounds(this);
        return (bounds) ? bounds : super.getBounds();
    }

    /**
     * Print the Metadata from this layer as html.
     */
    printMetadata() {
        return super.printMetadata({
            abstract: this.abstract,
            title: this.title,
            type: this.type
        })
    }

}
